// crawl-run.ts — turns one `CrawlRun` row into owner-facing text for the source detail screen
// (Task 5, source-detail-diagnostics). Everything below reads `run.outcome`, NEVER `run.error` —
// `outcome` is the fact the crawl handler already worked out (`classifyCrawlOutcome`,
// apps/api/src/jobs/handlers/crawl-outcome.ts); `error` is a transport string with no `hint:`
// convention on this table (that prefix lives on `DataSource.error` only, see Task 3's report)
// and re-deriving copy from it here would be parsing a string to recover a fact we already have
// typed. The one exception is `outcome === 'failed'`, where a CURATED `error` — see
// `isCuratedFailedMessage` below — is the only account of what went wrong and is surfaced
// verbatim; anything else (including a raw exception message) falls back to a generic line.
//
// House rule this file exists to enforce (source-detail-diagnostics spec + brief + review
// round 1, 2026-08-11):
//   - `truncated_capacity` is OUR OWN Cloudflare rendering capacity running out. Never name
//     Cloudflare, never say "rate limit", never suggest a plan or an upgrade — the owner did
//     nothing wrong and can't fix it by paying more. Review round 1 (Important 4c): naming it
//     "capacity" without saying WHOSE, right before `truncated_page_cap`'s message names the
//     owner's plan, let a reader infer their own plan is the constraint even though the word
//     "plan" never appears — fixed by saying "our own" explicitly, matching the wording
//     `crawl-source.ts`'s `RATE_LIMIT_ZERO_PAGES_MESSAGE` already uses for the same cause.
//   - `truncated_page_cap` is the ONE outcome where pointing at the plan is honest.
//   - `pagesRemaining` is a lower bound (BFS hasn't discovered everything) — always framed as
//     "at least N ... weren't indexed" (review round 1, Important 4a — matching
//     `crawl-source.ts`'s own `truncationRemainingClause` wording exactly; the original "still
//     waiting" verb promised a queue that doesn't exist, see that function's own doc comment for
//     why a resync can't "pick up" anything), never rendered as an exact count, and never
//     claiming continuation when it's 0. `truncated_page_cap`'s own message below never reads
//     `pagesRemaining` at all — "the most your plan's page cap allows right now" is honest with
//     no count attached, so there's no 0-vs-positive distinction to get wrong there (corrected,
//     final review: this bullet used to claim `pagesRemaining` is always 0 for that outcome "by
//     construction", pointing at a `CrawlRunDto` doc comment that never existed — it wasn't
//     always 0, `crawler/index.ts`'s loop just failed to record the real, frequently-positive
//     value on a page-cap exit; see that file's own `pagesRemaining` doc comment for the fix).
//   - A `complete` run with `failedCount > 0` is still complete: some individual pages failed,
//     the crawl itself finished. The copy below never uses the word "failed" for this case.
import { isHintMessage, isVerifyOwnedHint } from '@repo/shared-types';
import type { CrawlRunDto } from '@repo/shared-types';
import type { Catalog } from '../locales/en';

/** Matches `crawl-source.ts`'s own `truncationRemainingClause` wording exactly (review round 1,
 * Important 4a) — that function's own doc comment explains why 0 doesn't mean "nothing left",
 * only "nothing KNOWN is left" (BFS hasn't discovered the whole frontier), and why the verb must
 * never imply a resync continues from here: a resync restarts the crawl's BFS from the root
 * (`buildResyncChain`, apps/api/src/routes/sources.ts), so "still waiting" — a queue that
 * doesn't exist — was a second, worse dishonesty than the one this function exists to remove.
 * `t.sourceDetail.remainingAtLeast`/`remainingNone` (locale-dashboard plan, Task 9) carry the two
 * possible sentences; which one is true is still decided here, in code, not copy. */
function remainingClause(pagesRemaining: number, t: Catalog): string {
  if (pagesRemaining > 0) {
    return t.sourceDetail.remainingAtLeast({ n: pagesRemaining });
  }
  return t.sourceDetail.remainingNone;
}

/** The curated, owner-safe zero-pages messages `outcome: 'failed'` can carry
 * (`ZERO_PAGES_MESSAGE`/`offSiteRedirectMessage`, apps/api/src/jobs/handlers/crawl-source.ts)
 * all start with this exact phrase. A raw exception message — `CrawlRun.error` on the handler's
 * catch path is `err.message`/`String(err)` verbatim, a column its own doc comment calls
 * "free-text for admin/debugging" — never does (review round 1, Important 6: could be a Prisma
 * error, or a Cloudflare instance id, on a screen where naming Cloudflare is forbidden for every
 * other outcome). Anything that doesn't match this prefix falls back to a generic line instead
 * of rendering admin/debugging text to the owner. */
const CURATED_FAILED_MESSAGE_PREFIX = 'Crawl found 0 pages';

function isCuratedFailedMessage(error: string): boolean {
  return error.startsWith(CURATED_FAILED_MESSAGE_PREFIX);
}

/** Whether `source.error` is the CRAWL's own account of an event this file's "Latest crawl"
 * section would already narrate — either the `hint:`-prefixed truncation/near-empty notice
 * `crawl-source.ts` writes onto `DataSource.error` for a run that stays `indexing` (a distinct
 * convention from `CrawlRun.error`, which never carries `hint:` — see this file's own header
 * comment), or one of the curated zero-pages messages above (written straight to
 * `DataSource.error`, no `hint:` prefix, when the run instead lands on `error` status).
 *
 * Review round 2 — `SourceDetail.tsx` uses this to decide whether its header's error line would
 * just repeat "Latest crawl" in different words. The FIRST version of that check only asked
 * "does a run exist", which suppressed the header for ANY error once a run existed — but
 * `DataSource.error` is also written by `index_documents` ("Indexing failed for all N
 * document(s): …") and the job poller (`curatedJobFailureMessage`, apps/api/src/jobs/poller.ts —
 * "Something went wrong finishing this crawl…" / "Couldn't finish removing this source…") for
 * events a crawl run never mentions at all. A source that crawled fine (`outcome: 'complete'`) and then failed to index
 * every document showed an `error` chip, "Indexed 12 pages.", and NO explanation whatsoever —
 * the exact hole this function exists to close. Only an error that is actually the crawl's own
 * voice gets suppressed here; everything else (indexing/poller failures, or anything else this
 * table hasn't seen before) keeps rendering.
 *
 * Final whole-branch review, finding 4c — `isVerifyOwnedHint(error)` (`@repo/shared-types`,
 * canonical: this file and `verify-indexing.ts` share ONE definition) is now excluded from the
 * broad `isHintMessage` suppression, for `verify_indexing`'s own two hints specifically: the
 * partial-failure "N of M pages failed indexing" shape and the paused-instance hint. The
 * partial-failure hint already has a dedicated renderer (`CrawlDiagnostics`, below), so
 * excluding it here is mostly redundant with that panel for `url`/`sitemap` sources — accepted,
 * since the paused-instance hint has NO such dedicated renderer and, unlike the partial-failure
 * hint, can occur on `text`/`file` sources too (`showCrawlSection` in `SourceDetail.tsx` is
 * `false` for those, so `CrawlDiagnostics` never even mounts). Before this fix a paused instance
 * was completely invisible on Source Detail — suppressed here (any `hint:` message), never
 * rendered by `CrawlDiagnostics` (a paused instance leaves every document `pending`, so
 * `erroredCount` stays 0) — with a green, healthy-looking chip the entire time. Routing it
 * through the header (the one element every source type actually renders) rather than adding a
 * SECOND bespoke diagnostics-panel-for-text/file-sources is the smaller, more honest change:
 * these are exactly the "something needs a human / is degraded" messages the header exists for. */
export function isCrawlNarratedError(error: string): boolean {
  if (isVerifyOwnedHint(error)) return false;
  return isHintMessage(error) || isCuratedFailedMessage(error);
}

/** Owner-facing summary of one crawl run — the plain-language paragraph the source detail
 * screen shows above the page list. Pure and exhaustive over `CrawlOutcome`'s six members.
 * `t: Catalog` (locale-dashboard plan, Task 9) is required, not optional — every branch below
 * renders through it, so there is no code path left that can render English behind the active
 * locale's back. */
export function describeCrawlRun(
  run: Pick<CrawlRunDto, 'outcome' | 'pagesEmitted' | 'pagesRemaining' | 'failedCount' | 'error'>,
  t: Catalog,
): string {
  switch (run.outcome) {
    // "Crawled", not "Indexed" (final review, source-detail-diagnostics): the `CrawlRun` row is
    // written by `crawl-source.ts` BEFORE `index_documents` ever runs — indexing hasn't happened
    // yet at the moment this sentence is true. "Indexed N pages." collided head-on with
    // `index-documents.ts`'s "Indexing failed for all N document(s)" when indexing then failed
    // wholesale: the detail screen showed both, one claiming success at the exact thing the other
    // says failed. "Crawled" is true the instant the row is written and never contradicts
    // whatever indexing does afterward.
    case 'complete':
      if (run.failedCount <= 0) return t.sourceDetail.crawledPages({ n: run.pagesEmitted });
      return t.sourceDetail.crawledPagesWithSkipped({
        n: run.pagesEmitted,
        failedCount: run.failedCount,
      });
    case 'truncated_capacity':
      return (
        t.sourceDetail.truncatedCapacity({ n: run.pagesEmitted }) +
        remainingClause(run.pagesRemaining, t)
      );
    case 'truncated_deadline':
      return (
        t.sourceDetail.truncatedDeadline({ n: run.pagesEmitted }) +
        remainingClause(run.pagesRemaining, t)
      );
    case 'truncated_attempts':
      return (
        t.sourceDetail.truncatedAttempts({ n: run.pagesEmitted }) +
        remainingClause(run.pagesRemaining, t)
      );
    case 'truncated_page_cap':
      return t.sourceDetail.truncatedPageCap({ n: run.pagesEmitted });
    case 'failed':
      if (run.error && isCuratedFailedMessage(run.error)) return run.error;
      return t.sourceDetail.crawlFailedGeneric;
  }
}
